<script lang="ts" setup>
import { ref, computed } from "vue";
import { useConnectionDiagnostics } from "@/queries/connections";
import type { ConnectionDiagnostic, DiagnosticFilter } from "@/types";
import SkeletonLoader from "@/components/SkeletonLoader.vue";

const props = defineProps<{ connectionId: string }>();

const connectionId = computed(() => props.connectionId);
const filter = ref<DiagnosticFilter>("all");
const { diagnostics, diagnosticsLoading: loading, diagnosticsError: error, refetchDiagnostics } =
  useConnectionDiagnostics(connectionId, filter);

const filters: { id: DiagnosticFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "errors", label: "Errors" },
  { id: "setup", label: "Setup" },
  { id: "runtime", label: "Runtime" },
];

// Row expansion is keyed by index — diagnostics have no id, and the list is newest-first + stable.
const expanded = ref<number | null>(null);
function toggle(i: number) {
  expanded.value = expanded.value === i ? null : i;
}

function outcomeBadgeClass(d: ConnectionDiagnostic): string {
  switch (d.outcome) {
    case "Success": return "bg-success";
    case "Failure": return "bg-danger";
    case "Skipped": return "bg-secondary";
    default: return "bg-info";
  }
}

function fmt(d: string): string {
  return new Date(d).toLocaleString();
}

function duration(ms?: number | null): string {
  return ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

const hasWrites = (d: ConnectionDiagnostic) => Object.keys(d.attemptedWrites).length > 0;
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="btn-group btn-group-sm" role="group" aria-label="Filter diagnostics">
        <button
          v-for="f in filters"
          :key="f.id"
          type="button"
          class="btn"
          :class="filter === f.id ? 'btn-primary' : 'btn-outline-secondary'"
          @click="filter = f.id"
        >
          {{ f.label }}
        </button>
      </div>
      <button class="bb-btn-secondary-cta btn-sm" :disabled="loading" @click="refetchDiagnostics()">
        <i class="bi bi-arrow-clockwise me-1"></i> Refresh
      </button>
    </div>

    <SkeletonLoader v-if="loading || error" type="table" :rows="6" :error="error" @retry="refetchDiagnostics()" />

    <div v-else-if="diagnostics.length === 0" class="text-center text-muted py-5">
      <i class="bi bi-clipboard-x fs-3 d-block mb-2"></i>
      No diagnostic events{{ filter === "all" ? " yet" : " match this filter" }}.
    </div>

    <table v-else class="table table-hover align-middle mb-0">
      <thead>
        <tr>
          <th scope="col">When</th>
          <th scope="col">Operation</th>
          <th scope="col">Outcome</th>
          <th scope="col">Status</th>
          <th scope="col">Reason</th>
          <th scope="col" class="text-end"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(d, i) in diagnostics" :key="i">
          <tr role="button" @click="toggle(i)">
            <td class="text-nowrap">{{ fmt(d.occurredAt) }}</td>
            <td class="text-capitalize">{{ d.operation }}</td>
            <td><span class="badge" :class="outcomeBadgeClass(d)">{{ d.outcome }}</span></td>
            <td>{{ d.statusCode ?? "—" }}</td>
            <td class="text-truncate" style="max-width: 22rem">{{ d.reason ?? d.message ?? "—" }}</td>
            <td class="text-end">
              <i class="bi" :class="expanded === i ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </td>
          </tr>
          <tr v-if="expanded === i">
            <td colspan="6">
              <dl class="row mb-0 small">
                <template v-if="d.handler"><dt class="col-sm-3">Handler</dt><dd class="col-sm-9">{{ d.handler }}</dd></template>
                <template v-if="d.endpoint"><dt class="col-sm-3">Endpoint</dt><dd class="col-sm-9"><code>{{ d.endpoint }}</code></dd></template>
                <template v-if="d.vendorErrorCode"><dt class="col-sm-3">Vendor error</dt><dd class="col-sm-9"><code>{{ d.vendorErrorCode }}</code></dd></template>
                <template v-if="d.durationMs != null"><dt class="col-sm-3">Duration</dt><dd class="col-sm-9">{{ duration(d.durationMs) }}</dd></template>
                <template v-if="d.attempt != null"><dt class="col-sm-3">Attempt</dt><dd class="col-sm-9">{{ d.attempt }}</dd></template>
                <template v-if="d.lockWaitMs != null"><dt class="col-sm-3">Lock wait</dt><dd class="col-sm-9">{{ duration(d.lockWaitMs) }}</dd></template>
                <template v-if="d.message"><dt class="col-sm-3">Message</dt><dd class="col-sm-9">{{ d.message }}</dd></template>
                <template v-if="hasWrites(d)">
                  <dt class="col-sm-3">Attempted writes</dt>
                  <dd class="col-sm-9">
                    <div v-for="(value, key) in d.attemptedWrites" :key="key">
                      <span class="text-capitalize">{{ key }}</span>: <code>{{ value }}</code>
                    </div>
                    <div class="text-muted mt-1">Secret values are masked.</div>
                  </dd>
                </template>
              </dl>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
